import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Profiler, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { unstable_NormalPriority as NormalPriority, unstable_scheduleCallback as scheduleCallback } from 'scheduler';
import { v4 as createUUID } from 'uuid';
import { fg } from '@atlaskit/platform-feature-flags';
import coinflip from '../coinflip';
import { getInteractionRate } from '../config';
import { getActiveTrace, setInteractionActiveTrace } from '../experience-trace-id-context';
import UFOInteractionContext from '../interaction-context';
import UFOInteractionIDContext from '../interaction-id-context';
import { abortByNewInteraction, addApdex as _addApdex, addCustomData as _addCustomData, addCustomTiming, addHold, addHoldByID, addMark as _addMark, addNewInteraction, addProfilerTimings, addRequestInfo, addSegment, addSpan, removeHoldByID, removeSegment, tryComplete } from '../interaction-metrics';
import UFORouteName from '../route-name-context';
import generateId from '../short-id';
var tryCompleteHandle;

/** A portion of the page we apply measurement to */
export default function UFOSegment(_ref) {
  var segmentName = _ref.name,
    children = _ref.children;
  var parentContext = useContext(UFOInteractionContext);
  var segmentId = useMemo(function () {
    return generateId();
  }, []);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    segmentHighlight = _useState2[0],
    setSegmentHighlight = _useState2[1];
  var labelStack = useMemo(function () {
    return parentContext !== null && parentContext !== void 0 && parentContext.labelStack ? [].concat(_toConsumableArray(parentContext.labelStack), [{
      name: segmentName,
      segmentId: segmentId
    }]) : [{
      name: segmentName,
      segmentId: segmentId
    }];
  }, [parentContext, segmentName, segmentId]);
  var interactionId = useContext(UFOInteractionIDContext);
  var interactionContext = useMemo(function () {
    addSegment(labelStack);
    var lastCompleteEndTime = 0;
    function complete() {
      var endTime = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : performance.now();
      if (interactionId.current) {
        if (parentContext) {
          parentContext.complete();
        } else {
          var capturedInteractionId = interactionId.current;
          if (endTime > lastCompleteEndTime) {
            lastCompleteEndTime = endTime;
          }
          if (tryCompleteHandle) {
            var _cancelAnimationFrame;
            (_cancelAnimationFrame = cancelAnimationFrame) === null || _cancelAnimationFrame === void 0 || _cancelAnimationFrame(tryCompleteHandle);
          }
          scheduleCallback(NormalPriority, function () {
            if (capturedInteractionId === interactionId.current) {
              var _document;
              var isPageVisible = ((_document = document) === null || _document === void 0 ? void 0 : _document.visibilityState) === 'visible';
              var canDoRAF = typeof requestAnimationFrame !== 'undefined';
              if (isPageVisible && canDoRAF) {
                tryCompleteHandle = requestAnimationFrame(function () {
                  tryCompleteHandle = requestAnimationFrame(function () {
                    if (capturedInteractionId === interactionId.current) {
                      tryComplete(interactionId.current, lastCompleteEndTime);
                    }
                  });
                });
              } else {
                tryComplete(interactionId.current, lastCompleteEndTime);
              }
            }
          });
        }
      }
    }
    function _internalHold(labelStack, name
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    ) {
      if (interactionId.current != null) {
        if (parentContext) {
          return parentContext._internalHold(labelStack, name);
        } else {
          var capturedInteractionId = interactionId.current;
          var disposeHold = addHold(interactionId.current, labelStack, name);
          return function () {
            if (capturedInteractionId === interactionId.current) {
              disposeHold();
            }
          };
        }
      }
    }
    function _internalHoldByID(labelStack, id, name, remove
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    ) {
      if (interactionId.current != null) {
        if (parentContext) {
          parentContext._internalHoldByID(labelStack, name, id, remove);
          return;
        }
        if (!remove) {
          addHoldByID(interactionId.current, labelStack, name, id);
        } else {
          removeHoldByID(interactionId.current, id);
        }
      }
    }
    if (parentContext) {
      return _objectSpread(_objectSpread({}, parentContext), {}, {
        labelStack: labelStack,
        complete: complete
      });
    }
    return {
      labelStack: labelStack,
      hold: function hold() {
        var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'unknown';
        return this._internalHold(this.labelStack, name);
      },
      addHoldByID: function addHoldByID(labelStack, id) {
        var name = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'unknown';
        this._internalHoldByID(labelStack, id, name, false);
      },
      removeHoldByID: function removeHoldByID(labelStack, id) {
        var name = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'unknown';
        this._internalHoldByID(labelStack, id, name, true);
      },
      tracePress: function tracePress() {
        var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'unknown';
        var timestamp = arguments.length > 1 ? arguments[1] : undefined;
        if (interactionId.current != null) {
          abortByNewInteraction(interactionId.current, name);
        }
        var rate = getInteractionRate(name, 'press');
        if (coinflip(rate)) {
          var startTimestamp = timestamp !== null && timestamp !== void 0 ? timestamp : performance.now();
          var newId = createUUID();
          interactionId.current = newId;
          // covered experiences with tracing instrumentation:
          // inline-result.global-issue.create-modal.submit
          // inline-result.global-issue.create-modal-subsequent
          setInteractionActiveTrace(newId, 'press');
          addNewInteraction(newId, name, 'press', startTimestamp, rate, this.labelStack, UFORouteName.current, getActiveTrace());
        }
      },
      retainQuery: function retainQuery(info) {
        if (interactionId.current != null) {
          addRequestInfo(interactionId.current, this.labelStack, info);
        }
      },
      addPreload: function addPreload(moduleId, timestamp) {
        if (interactionId.current != null) {
          _addMark(interactionId.current, 'bundle_preload', moduleId, this.labelStack, timestamp);
        }
      },
      addLoad: function addLoad(identifier, start, end) {
        if (interactionId.current != null) {
          var _this$labelStack = this.labelStack,
            _labelStack = _this$labelStack === void 0 ? [] : _this$labelStack;
          addSpan(interactionId.current, 'bundle_load', identifier, _labelStack, start, end);
        }
      },
      addMark: function addMark(name, timestamp
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      ) {
        if (interactionId.current != null) {
          var time = timestamp !== null && timestamp !== void 0 ? timestamp : performance.now();
          _addMark(interactionId.current, 'custom', name, this.labelStack, time);
        }
      },
      addCustomData: function addCustomData(customData) {
        if (interactionId.current != null) {
          _addCustomData(interactionId.current, this.labelStack, customData);
        }
      },
      addCustomTimings: function addCustomTimings(customTimings) {
        if (interactionId.current != null) {
          addCustomTiming(interactionId.current, this.labelStack, customTimings);
        }
      },
      addApdex: function addApdex(apdexInfo) {
        if (interactionId.current != null) {
          var key = apdexInfo.key,
            stopTime = apdexInfo.stopTime,
            startTime = apdexInfo.startTime;
          _addApdex(interactionId.current, {
            key: key,
            stopTime: stopTime,
            startTime: startTime,
            labelStack: this.labelStack
          });
        }
      },
      onRender: function onRender(phase, actualDuration, baseDuration, startTime, commitTime) {
        if (interactionId.current !== null) {
          addProfilerTimings(interactionId.current, this.labelStack, phase, actualDuration, baseDuration, startTime, commitTime);
          this.complete(commitTime);
        }
      },
      _internalHold: _internalHold,
      _internalHoldByID: _internalHoldByID,
      complete: complete
    };
  }, [parentContext, labelStack, interactionId]);
  var onRender = useCallback(function (_id, phase, actualDuration, baseDuration, startTime, commitTime) {
    interactionContext.onRender(phase, actualDuration, baseDuration, startTime, commitTime);
  }, [interactionContext]);
  useEffect(function () {
    try {
      var shouldHighlightSegments = sessionStorage.getItem('segmentsHighlight') === 'true';
      setSegmentHighlight(shouldHighlightSegments);
    } catch (err) {
      // do nothing
    }
  }, []);
  useEffect(function () {
    return function () {
      removeSegment(labelStack);
    };
  }, [interactionId, parentContext, interactionContext, labelStack]);
  var reactProfilerId = useMemo(function () {
    return labelStack.map(function (l) {
      return l.name;
    }).join('/');
  }, [labelStack]);
  return /*#__PURE__*/React.createElement(UFOInteractionContext.Provider, {
    value: interactionContext
  }, /*#__PURE__*/React.createElement(Profiler, {
    id: reactProfilerId,
    onRender: onRender
  }, children, segmentHighlight && fg('platform_ufo_dev_tool_segment_visualiser') &&
  /*#__PURE__*/
  // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
  React.createElement("span", {
    "data-segment-name": segmentName,
    style: {
      display: 'none'
    }
  })));
}